import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { addTimezoneLocal, asyncLocalStorage, getURLParams, isOrgUser, removeCommas, tryParseJSON } from '../../utils';
import { isAuth, signOut } from '../../helpers/auth';
import {
     ACCOUNT_SETTINGS_PATH,
     AUTH_PATH,
     GUEST_PATH,
     LOCAL_CART,
     LOCAL_COUPON,
     LOCAL_COUPON_VOUCHERS,
     LOCAL_LOGIN_AS_SESSION,
     LOCAL_ORG_USER,
     MY_LEARNING_PATH,
     MY_ORG_PATH,
     ORG_USER_ROLES,
     POPUP_CHOOSE_TRAINING_EVENT_DATE,
     SUBSCRIBER_PATH,
} from '../../constants';

import { subscriberRoutes, organizationRoutes } from '../../routes';

import ConfirmCountry from './ConfirmCountry';
import GuestHeader from '../guest/TheHeader';
import GuestFooter from '../guest/TheFooter';
import SubscriberContent from './TheContent';
import SubscriberHeader from './TheHeader';
import SubscriberOrgHeader from './TheHeaderOrg';
import TheContentOrg from './TheContentOrg';
import TheLayoutGuest from '../guest/TheLayout';
import PopupEnabledTestMode from './PopupEnabledTestMode';
import PopupDisabledTestMode from './PopupDisabledTestMode';

import Licensees from '../../components/CMS/Subscriber/Licensees';
import SellingGroupBanner from '../../components/General/Banner/SellingGroupBanner';
import ConfirmPasswordAndFullNamePopup from '../../components/General/Popups/ConfirmPasswordAndFullNamePopup';
import ConfirmProfessionalDetailsPopup from '../../components/General/Popups/ConfirmProfessionalDetailsPopup';
import { PopupChooseTrainingEventDate } from '../../components/General/Popups';

import { setSubscriberUser } from '../../components/CMS/Subscriber/GlobalStates/CurrentUser/actions';
import { fetchSubscriberOrdersRequest } from '../../components/CMS/Guest/Checkout/action';

import SubscriberPusher from './ThePusher';

const TheLayoutSubscriber = React.memo(() => {
     const location = useLocation();
     const orgUser = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));

     const HeaderLayout = () => {
          if (!orgUser) {
               return <SubscriberHeader />;
          }

          return <GuestHeader />;
     };

     return (
          <div className="body-wrapper">
               {<HeaderLayout />}
               {location.pathname === GUEST_PATH.SELLING_GROUPS && <SellingGroupBanner />}
               <SubscriberContent />
               <GuestFooter />
               <Licensees />
          </div>
     );
});

const TheLayoutOrg = React.memo(() => {
     return (
          <div className="layout-org body-wrapper">
               <SubscriberOrgHeader />
               <div className="container-content w-100">
                    <TheContentOrg />
               </div>
               <GuestFooter />
          </div>
     );
});

const Subscriber = () => {
     const location = useLocation();
     const navigate = useNavigate();
     const dispatch = useDispatch();

     const show = useSelector((state) => state.general.show);
     const user = useSelector((state) => state.subscriber.user);
     const licensees = useSelector((state) => state.subscriber.licensees);
     const orders = useSelector((state) => state.subscriber.ordersUser?.orders);
     const userSeats = useSelector((state) => state.subscriber.userSeats);
     const showLoginAs = useSelector((state) => state.general.showLoginAs);

     const orgUser = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));
     const localLoginAsSession = tryParseJSON(localStorage.getItem(LOCAL_LOGIN_AS_SESSION));
     const { pathname } = location;

     const organizationUser = Object.entries(MY_ORG_PATH).some(([_, value]) => pathname.includes(value));

     const orgId = orgUser?.organizationId;

     const showMyAccount =
          Object.entries(MY_LEARNING_PATH).some(([_, value]) => pathname.includes(value)) ||
          Object.entries(ACCOUNT_SETTINGS_PATH).some(([_, value]) => pathname.includes(value));

     const newPathName = pathname && pathname !== '/' ? (removeCommas(pathname, '/') ? removeCommas(pathname, '/').split('/') : '') : '';
     const subscriberRoutesArrPath = subscriberRoutes.filter(({ path }) => path !== '*').map(({ path }) => removeCommas(path, '/'));
     const orgRoutesArrPath = organizationRoutes.filter(({ path }) => path !== '*').map(({ path }) => removeCommas(path, '/'));
     const isPathClaimSeat = ['accept-your-seats', 'verify-seat'].includes(newPathName[0]);

     const numberOfIndividualOrders = orders?.filter((item) => !item.organizationId).length;
     const numberOfOrgOrders = orders?.filter((item) => item.organizationId).length;

     const checkPurchaseIndividualFirst = numberOfIndividualOrders === 1;
     const checkHasUserSeat = userSeats?.length > 0 ? true : false;
     const checkPurchaseOrgFirst = numberOfIndividualOrders === 0 && numberOfOrgOrders > 0;

     const fetchData = () => {
          if (!orders) {
               dispatch(fetchSubscriberOrdersRequest());
          }
     };

     useEffect(fetchData, []); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          const timerId = setTimeout(() => {
               const roleOrgUser = user?.organizations?.find((org) => org.id === orgId)?.OrganizationUsers?.role ?? null;

               if (localStorage.getItem(LOCAL_ORG_USER) && showMyAccount) {
                    navigate(MY_ORG_PATH.ORGANIZATIONS_DASHBOARD);
                    return;
               }

               if ((!roleOrgUser || roleOrgUser === ORG_USER_ROLES.USER) && localStorage.getItem(LOCAL_ORG_USER)) {
                    localStorage.removeItem(LOCAL_ORG_USER);
                    !isPathClaimSeat && navigate(MY_LEARNING_PATH.DASHBOARD);
               }
          }, 100);

          return () => {
               clearTimeout(timerId);
          };
     }, [organizationUser]); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          const params = getURLParams();
          if (params?.fromEmail !== 'inviteManager') {
               return;
          }

          if (params.userId !== user?.id) {
               signOut(false, () => {
                    dispatch(setSubscriberUser(null));
               });
               navigate(AUTH_PATH.LOGIN);
               return;
          }
          navigate(MY_LEARNING_PATH.DASHBOARD);
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          if ((subscriberRoutesArrPath.includes(newPathName[0]) && orgUser) || (isAuth() && !orgUser && orgRoutesArrPath.includes(newPathName[0]))) {
               localStorage.removeItem(LOCAL_CART);
               localStorage.removeItem(LOCAL_COUPON);
               localStorage.removeItem(LOCAL_COUPON_VOUCHERS);

               const fullPath = `${pathname}${location.search}${location.hash}`;
               const redirectPath = orgUser ? fullPath : SUBSCRIBER_PATH.DASHBOARD;

               asyncLocalStorage.removeItem(LOCAL_ORG_USER).then(() => {
                    addTimezoneLocal(user?.timezone);
                    navigate(redirectPath, { replace: true });
               });
          }
     }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

     const isAdmin = showLoginAs || localLoginAsSession;
     const isFirstOrder = checkPurchaseIndividualFirst || checkPurchaseOrgFirst || checkHasUserSeat;
     const isUserHaveTimezone = user && user.timezone;
     const isCountryExists = user?.country ? licensees?.some((li) => li.country.includes(user.country)) : false;

     const checkShowConfirmPasswordFullName = !isAdmin && isUserHaveTimezone && !user.firstLogin;

     const checkShowConfirmCountry = !isAdmin && !isPathClaimSeat && user && (!user.timezone || !user.country || !isCountryExists);

     const checkShowPopupProDetail =
          !checkShowConfirmCountry && !isAdmin && isFirstOrder && !isOrgUser() && user && !user.state && MY_LEARNING_PATH.DASHBOARD === pathname;

     const checkShowPopupChooseTrainingEventDate = show === POPUP_CHOOSE_TRAINING_EVENT_DATE && !checkShowConfirmCountry && !checkShowPopupProDetail;

     return (
          <>
               <SubscriberPusher />
               <PopupEnabledTestMode />
               <PopupDisabledTestMode />

               {showMyAccount ? <TheLayoutSubscriber /> : <>{organizationUser ? <TheLayoutOrg /> : <TheLayoutGuest />}</>}
               {checkShowConfirmPasswordFullName && <ConfirmPasswordAndFullNamePopup />}
               {checkShowPopupProDetail && <ConfirmProfessionalDetailsPopup />}
               {checkShowConfirmCountry && <ConfirmCountry />}
               {checkShowPopupChooseTrainingEventDate && <PopupChooseTrainingEventDate />}
          </>
     );
};

export default React.memo(Subscriber);
